/** Linha do tempo do cliente: notas, reuniões, marcos e decisões com data. Fase 4. */
import { useEffect, useState } from "react";
import type { FormEvent } from "react";
import { Link, useParams } from "react-router";
import { api } from "../../lib/api";
import { AdminShell } from "../../ui/AdminShell";
import { Button } from "../../ui/Button";
import { Card, EmptyState, Skeleton } from "../../ui/Card";
import { Input, TextArea } from "../../ui/Input";
import { Select } from "../../ui/Select";

interface Entry {
  id: string;
  kind: "nota" | "reuniao" | "marco" | "decisao";
  title: string;
  body: string | null;
  eventDate: string;
  createdAt: string;
}

const KIND_LABEL: Record<Entry["kind"], string> = {
  nota: "Nota", reuniao: "Reunião", marco: "Marco", decisao: "Decisão",
};

function today(): string {
  return new Date().toISOString().slice(0, 10);
}

export function Timeline() {
  const { id } = useParams();
  const [entries, setEntries] = useState<Entry[] | null>(null);
  const [kind, setKind] = useState<Entry["kind"]>("nota");
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [eventDate, setEventDate] = useState(today());
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  async function load() {
    setEntries(await api.get<Entry[]>(`/clients/${id}/timeline`).catch(() => []));
  }
  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  async function adicionar(e: FormEvent) {
    e.preventDefault();
    if (title.trim() === "") return;
    setSaving(true);
    try {
      await api.post(`/clients/${id}/timeline`, { kind, title, body, eventDate });
      setTitle("");
      setBody("");
      setKind("nota");
      setEventDate(today());
      await load();
    } finally {
      setSaving(false);
    }
  }

  async function excluir(entryId: string) {
    if (!window.confirm("Tem certeza que deseja excluir este evento da linha do tempo?")) return;
    setDeletingId(entryId);
    try {
      await api.del(`/clients/${id}/timeline/${entryId}`);
      await load();
    } catch {
      alert("Não foi possível excluir o evento. Tente novamente.");
    } finally {
      setDeletingId(null);
    }
  }

  return (
    <AdminShell>
      <Link to={`/clientes/${id}`} className="type-caption text-text-tertiary">← Voltar ao cliente</Link>
      <h1 className="type-h1 mt-1 text-text-cream">Linha do tempo</h1>
      <p className="type-body-sm mt-1 text-text-secondary">
        Reuniões, marcos e decisões — o histórico de tudo que aconteceu com este cliente.
      </p>

      <Card className="mt-6">
        <form onSubmit={adicionar} className="flex flex-col gap-4">
          <div className="grid grid-cols-2 gap-3">
            <Select label="Tipo" value={kind} onChange={(e) => setKind(e.target.value as Entry["kind"])}>
              <option value="nota">Nota</option>
              <option value="reuniao">Reunião</option>
              <option value="marco">Marco</option>
              <option value="decisao">Decisão</option>
            </Select>
            <Input label="Data" type="date" value={eventDate} onChange={(e) => setEventDate(e.target.value)} />
          </div>
          <Input label="Título" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Ex.: Reunião de alinhamento" />
          <TextArea label="Detalhes (opcional)" value={body} onChange={(e) => setBody(e.target.value)} rows={3} />
          <div>
            <Button type="submit" loading={saving}>Adicionar ao histórico</Button>
          </div>
        </form>
      </Card>

      {entries === null && <Skeleton className="mt-6 h-24" />}

      {entries?.length === 0 && (
        <div className="mt-8">
          <EmptyState title="Nada registrado ainda" description="Adicione o primeiro evento acima." />
        </div>
      )}

      {entries !== null && entries.length > 0 && (
        <div className="mt-6 flex flex-col gap-3">
          {entries.map((e) => (
            <Card key={e.id}>
              <div className="flex items-start justify-between gap-3">
                <div>
                  <div className="flex items-center gap-2">
                    <span className="type-overline text-gold">{KIND_LABEL[e.kind]}</span>
                    <span className="type-caption text-text-tertiary">
                      {new Date(e.eventDate).toLocaleDateString("pt-BR")}
                    </span>
                  </div>
                  <h2 className="type-h3 mt-1 text-text-cream">{e.title}</h2>
                  {e.body !== null && (
                    <p className="type-body-sm mt-1 whitespace-pre-wrap text-text-secondary">{e.body}</p>
                  )}
                </div>
                <Button size="sm" variant="ghost" onClick={() => excluir(e.id)} loading={deletingId === e.id}>Excluir</Button>
              </div>
            </Card>
          ))}
        </div>
      )}
    </AdminShell>
  );
}
